/**
 * PictureUp on its own host.
 *
 * The server can serve PictureUp (the A-Roll SaaS, /aroll) from its own
 * origin, AROLL_APP_URL, with the same SPA. index.html loads
 * /api/auth/runtime-config.js before the app boots, which sets
 * window.__KG_RUNTIME__ = { arollAppUrl, crmAppUrl } (both null while
 * PictureUp has no host of its own, and the file may not load at all in
 * dev). Tokens live in localStorage, so the two origins are two separate
 * logins; a person who signs in on the wrong one is handed to the other
 * through /sso (tokens in the URL fragment, the public-site login's
 * pattern).
 *
 * The decisions are pure functions of the origins, so they're
 * unit-tested from the backend suite; the window-reading wrappers sit at
 * the bottom.
 */

export type AdsRuntimeConfig = {
  metaPixelId?: string;
  googleAdsId?: string;
  googleAdsSignupLabel?: string;
  linkedinPartnerId?: string;
  linkedinSignupConversionId?: string;
};

export type RuntimeConfig = {
  arollAppUrl: string | null;
  crmAppUrl: string | null;
  /** PictureUp's public sales site (AROLL_SITE_URL), for the terms and
   *  privacy links; absent = kilogram.com.au/a-roll. */
  siteUrl?: string;
  /** Ad platform ids for the signup pages (ADS_* env); absent = no tags. */
  ads?: AdsRuntimeConfig;
};

declare global {
  interface Window {
    __KG_RUNTIME__?: Partial<RuntimeConfig>;
  }
}

function originOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** True when this page is PictureUp's own host. */
export function isPictureUpOrigin(currentOrigin: string, cfg: Partial<RuntimeConfig> | undefined): boolean {
  const aroll = originOf(cfg?.arollAppUrl);
  return !!aroll && aroll === currentOrigin;
}

/** Where PictureUp links should point: its own host, else this page's. */
export function arollLinkOriginFor(currentOrigin: string, cfg: Partial<RuntimeConfig> | undefined): string {
  return originOf(cfg?.arollAppUrl) ?? currentOrigin;
}

/** Paths the PictureUp host serves itself; everything else opens /aroll. */
export function isPictureUpPath(pathname: string): boolean {
  return pathname === '/aroll' || pathname.startsWith('/aroll/') || pathname === '/sso';
}

type HandoffUser = { role: string; tenantId?: string | null };

/**
 * Which way a signed-in person must be handed off, given where they are.
 *   - On the PictureUp host, anyone who is not a workspace member
 *     (Kilogram staff, portal clients) goes to the CRM.
 *   - On the CRM host, a workspace member goes to PictureUp, but only
 *     when PictureUp has its own host (else /aroll here, as before).
 * Null = they are in the right place.
 */
export function handoffFor(
  onPictureUpHost: boolean,
  user: HandoffUser,
  cfg: Partial<RuntimeConfig> | undefined,
): { to: 'crm' | 'pictureup'; origin: string; note: 'staff' | 'client' | 'workspace' } | null {
  const aroll = originOf(cfg?.arollAppUrl);
  const crm = originOf(cfg?.crmAppUrl);
  if (!aroll || !crm || aroll === crm) return null;
  if (onPictureUpHost) {
    if (user.tenantId) return null;
    return { to: 'crm', origin: crm, note: user.role === 'CLIENT' ? 'client' : 'staff' };
  }
  if (user.tenantId && user.role !== 'CLIENT') return { to: 'pictureup', origin: aroll, note: 'workspace' };
  return null;
}

/** The note shown for the second before the hand-off. */
export const HANDOFF_NOTES = {
  staff: 'This is a Kilogram staff account. Opening the CRM.',
  client: 'This is a Kilogram client account. Opening your portal.',
  workspace: 'This is a PictureUp account. Opening PictureUp.',
} as const;

/**
 * The /sso URL that signs the person in on the other origin. Same
 * fragment shape SsoHandoff.tsx reads: access, refresh, and the user as
 * base64 of the URI-encoded JSON. Fragments never reach a server.
 */
export function ssoHandoffUrl(
  origin: string,
  tokens: { accessToken: string; refreshToken: string },
  user: unknown,
  encode: (s: string) => string = (s) => btoa(s),
): string {
  const params = new URLSearchParams({
    access: tokens.accessToken,
    refresh: tokens.refreshToken,
    user: encode(encodeURIComponent(JSON.stringify(user))),
  });
  return `${origin}/sso#${params.toString()}`;
}

// --- window wrappers ------------------------------------------------

function cfg(): Partial<RuntimeConfig> | undefined {
  return typeof window === 'undefined' ? undefined : window.__KG_RUNTIME__;
}

export function onPictureUpHost(): boolean {
  return typeof window !== 'undefined' && isPictureUpOrigin(window.location.origin, cfg());
}

/** Origin for PictureUp links (share links, room links). */
export function arollLinkOrigin(): string {
  return arollLinkOriginFor(typeof window === 'undefined' ? '' : window.location.origin, cfg());
}

export function currentHandoff(user: HandoffUser | null) {
  if (!user) return null;
  return handoffFor(onPictureUpHost(), user, cfg());
}
